import { useCallback, useState, useRef, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { useQuestionAndAnswerStore, useRoomConfigStore, useRoomStore } from '@src/stores';
import { useMyQuestions, useQuestionAndAnswerFilter } from '@src/hooks/queries/useWebinarQuery';

import { QUESTION_FILTERS, SORT_OPTIONS } from '@src/constants';

import { EAnswerType, EUserType, IQuestion, TShowFilter, TSortFilter, TUserType } from '@src/types';

const useQuestionAndAnswer = () => {
  const { currentPeerType } = useRoomContext();

  const [sortFilter, setSortFilter] = useState<TSortFilter>(SORT_OPTIONS[0].value as TSortFilter);
  const [isReplyingPrivately, setIsReplyingPrivately] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<IQuestion | null>(null);
  const [openedAnserSections, setOpenedAnserSections] = useState<Record<string, boolean>>({});
  const [showFilter, setShowFilter] = useState<TShowFilter>(
    QUESTION_FILTERS[currentPeerType as TUserType][0].value as TShowFilter
  );

  const { roomId } = useRoomStore(useShallow((state) => ({ roomId: state.roomId })));

  const { filteredQuestions, isAnswerBoxOpened, setQuestions, setIsAnswerBoxOpened } = useQuestionAndAnswerStore(
    useShallow((state) => ({
      filteredQuestions: state.questions,
      setQuestions: state.setQuestions,
      isAnswerBoxOpened: state.isAnswerBoxOpened,
      setIsAnswerBoxOpened: state.setIsAnswerBoxOpened,
    }))
  );

  const { data: { result: questionsFilteredData } = {}, isLoading } = useQuestionAndAnswerFilter(
    roomId,
    showFilter,
    sortFilter
  );

  const { data: myQuestionsList } = useMyQuestions(roomId, currentPeerType === EUserType.ATTENDEE);

  const questionsListRef = useRef<HTMLDivElement>(null);
  const prevQuestionsCountRef = useRef<number>(0);

  const { isSubmitQuestionsButtonEnabled, isAnswerQuestionsButtonEnabled } = useRoomConfigStore(
    useShallow((state) => ({
      isSubmitQuestionsButtonEnabled: state.questionsConfig.submitQuestionsButton,
      isAnswerQuestionsButtonEnabled: state.questionsConfig.answerQuestionButton,
    }))
  );

  const handleReplayPrivatelyCheck = useCallback((isChecked: boolean) => {
    setIsReplyingPrivately(isChecked);
  }, []);

  const handleOpenAnswerBox = useCallback(
    ({ question, answerType }: { question: IQuestion; answerType: EAnswerType }) => {
      setIsAnswerBoxOpened(true);
      setSelectedQuestion(question);
      setIsReplyingPrivately(answerType === EAnswerType.PRIVATE);
    },
    [setIsAnswerBoxOpened]
  );

  const handleCloseAnswerBox = useCallback(
    (posted: boolean) => {
      setIsAnswerBoxOpened(false);
      posted &&
        selectedQuestion?.id &&
        setOpenedAnserSections((prev) => ({
          ...prev,
          [selectedQuestion.id || '']: true, // Mark the question as answered
        }));
    },
    [selectedQuestion?.id, setIsAnswerBoxOpened]
  );

  const handleToggleAnswerSection = useCallback((questionId: string) => {
    setOpenedAnserSections((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  }, []);

  useEffect(() => {
    setShowFilter(QUESTION_FILTERS[currentPeerType as TUserType][0].value as TShowFilter);
  }, [currentPeerType]);

  useEffect(() => {
    myQuestionsList && useQuestionAndAnswerStore.setState({ myQuestionsList });
  }, [myQuestionsList]);

  useEffect(() => {
    questionsFilteredData && setQuestions(questionsFilteredData);
  }, [questionsFilteredData, setQuestions]);

  useEffect(() => {
    const list = questionsListRef.current;
    const isNewest = sortFilter === 'NEWEST';
    const currentCount = filteredQuestions?.length || 0;
    //set default value to filteredQuestions?.length in first render
    const prevCount = prevQuestionsCountRef.current || filteredQuestions?.length;
    if (list && currentCount !== prevCount) {
      list.scrollTo({
        top: isNewest ? 0 : list.scrollHeight,
        behavior: 'smooth',
      });
    }
    prevQuestionsCountRef.current = currentCount;
  }, [filteredQuestions, sortFilter]);

  return {
    filteredQuestions,
    isLoading,
    currentPeerType,
    showFilter,
    sortFilter,
    questionsListRef,

    selectedQuestion,
    isAnswerBoxOpened,
    openedAnserSections,
    isReplyingPrivately,
    isAnswerQuestionsButtonEnabled,
    isSubmitQuestionsButtonEnabled,

    setShowFilter,
    setSortFilter,
    handleOpenAnswerBox,
    handleCloseAnswerBox,
    handleToggleAnswerSection,
    handleReplayPrivatelyCheck,
  };
};

export default useQuestionAndAnswer;
